import { useParams, Link } from 'react-router-dom'
import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { Layout } from '../components/layout/Layout'
import { useAlbum } from '../hooks/useAlbums'
import { useImages, useDeleteImage, useRefreshExif } from '../hooks/useImages'
import { adminApiClient } from '../api/client'
import { ArrowLeft, Image as ImageIcon, Plus, Trash2, RefreshCw } from 'lucide-react'
import { VisibilityBadge } from '../components/common/VisibilityBadge'
import { getImageUrl } from '../lib/images'
import { groupImagesByDate } from '../lib/dateGroups'
import { DateHeader } from '../components/gallery/DateHeader'
import { ConfirmDialog } from '../components/common/ConfirmDialog'
import { BulkRefreshPanel } from '../components/admin/BulkRefreshPanel'
import { useBulkRefreshStore } from '../stores/bulkRefreshStore'

export const AdminAlbumDetail = () => {
  const { id } = useParams<{ id: string }>()
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [imageToDelete, setImageToDelete] = useState<string | null>(null)
  const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false)
  const [refreshingId, setRefreshingId] = useState<string | null>(null)

  const { data: album, isLoading: albumLoading } = useAlbum(id!, adminApiClient)
  const { data: imagesData, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading: imagesLoading } = useImages(adminApiClient, { 
    albumId: id,
  })
  const deleteImage = useDeleteImage(adminApiClient)
  const refreshExif = useRefreshExif(adminApiClient)
  const startBulkRefresh = useBulkRefreshStore((state) => state.startBulkRefresh)
  const bulkRun = useBulkRefreshStore((state) => (id ? state.runs[id] : undefined))
  const bulkRefreshActive = bulkRun?.status === 'collecting' || bulkRun?.status === 'running'

  const images = useMemo(() => {
    const seen = new Set<string>()
    return imagesData?.pages.flatMap((page) => page.items).filter((image) => {
      if (seen.has(image.imageId)) return false
      seen.add(image.imageId)
      return true
    }) || []
  }, [imagesData?.pages])

  const groupedItems = useMemo(() => groupImagesByDate(images), [images])

  const observerRef = useRef<IntersectionObserver | null>(null)
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
    if (!node) return
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: '200px' }
    )
    observerRef.current.observe(node)
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [])

  const handleDelete = (imageId: string) => {
    setImageToDelete(imageId)
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (imageToDelete) {
      deleteImage.mutate(imageToDelete)
      setDeleteConfirmOpen(false)
      setImageToDelete(null)
    }
  }

  const handleRefreshExif = async (imageId: string) => {
    setRefreshingId(imageId)
    try {
      await refreshExif.mutateAsync(imageId)
    } catch (error) {
      console.error('Failed to refresh EXIF:', error)
    }
    setRefreshingId(null)
  }

  const handleBulkRefresh = async () => {
    if (!id) return
    await startBulkRefresh(adminApiClient, id)
  }

  const isLoading = albumLoading || imagesLoading

  if (isLoading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-square bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </Layout>
    )
  }

  if (!album) {
    return (
      <Layout>
        <div className="text-center py-20">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Album not found</h1>
          <Link to="/admin/albums" className="text-blue-600 hover:text-blue-700">
            Back to Albums
          </Link>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="mb-6">
        <Link to="/admin/albums" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-5 h-5" />
          Back to Albums
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{album.name}</h1>
            {album.description && <p className="text-gray-600 mt-2">{album.description}</p>}
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <ImageIcon className="w-4 h-4" />
                {images.length} photos
              </span>
              <VisibilityBadge visibility={album.visibility} />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setRefreshConfirmOpen(true)}
              disabled={bulkRefreshActive || images.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Regenerate thumbnails for all images"
            >
              <RefreshCw className={`w-4 h-4 ${bulkRefreshActive ? 'animate-spin' : ''}`} />
              {bulkRefreshActive ? 'Refreshing…' : 'Refresh All'}
            </button>
            <Link to="/admin/upload" className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Plus className="w-4 h-4" />
              Add Photos
            </Link>
          </div>
        </div>
      </div>

      {id && <BulkRefreshPanel albumId={id} images={images} />}

      {images.length === 0 && !isFetchingNextPage ? (
        <div className="text-center py-20 bg-gray-50 rounded-2xl">
          <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No photos yet</h3>
          <Link to="/admin/upload" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus className="w-5 h-5" />
            Upload Photos
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {groupedItems.map((item) =>
              item.type === 'header' ? (
                <DateHeader key={`header-${item.date}`} date={item.date} />
              ) : (
                <div key={item.image.imageId} className="group relative bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                  <div className="aspect-square">
                    <img src={getImageUrl(item.image.thumbnailKeys?.small || item.image.originalKey)} alt={item.image.filename} className="w-full h-full object-cover" />
                    
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleRefreshExif(item.image.imageId)}
                        disabled={refreshExif.isPending}
                        className="p-2 bg-white rounded-lg hover:bg-gray-100 disabled:opacity-50"
                        title="Refresh EXIF"
                      >
                        <RefreshCw className={`w-4 h-4 ${refreshingId === item.image.imageId ? 'animate-spin' : ''}`} />
                      </button>
                      <button onClick={() => handleDelete(item.image.imageId)} className="p-2 bg-white rounded-lg hover:bg-red-50 text-red-600" aria-label="Delete image" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="p-3">
                    <p className="font-medium text-gray-900 truncate">{item.image.filename}</p>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        inherits {album.visibility}
                      </span>
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>

          <div ref={sentinelRef} className="h-10 flex justify-center items-center">
            {isFetchingNextPage && (
              <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteConfirmOpen(false)
          setImageToDelete(null)
        }}
        title="Delete Image"
        message="Are you sure you want to delete this image? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />

      <ConfirmDialog
        isOpen={refreshConfirmOpen}
        onConfirm={() => {
          setRefreshConfirmOpen(false)
          handleBulkRefresh()
        }}
        onCancel={() => setRefreshConfirmOpen(false)}
        title="Refresh All Thumbnails"
        message={
          images.length > 0
            ? `This will regenerate thumbnails and EXIF data for all ${images.length} images in this album, processed in small batches. You can watch progress and stop at any time.`
            : 'There are no images to refresh.'
        }
        confirmText="Start Refresh"
        variant="primary"
      />
    </Layout>
  )
}